import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import ScoreRing from '../components/ScoreRing';
import { Button } from '../components/ui/button';
import {
  Brain, Target, TrendingUp, GraduationCap, Wallet, ArrowRight, CheckCircle,
  CircleDashed, Compass, Sparkles,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import PageArrival from '../components/PageArrival';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENGINE_ICON = {
  career_fit: Sparkles, skills: Brain, transition: TrendingUp,
  future_of_work: Target, college: GraduationCap, funding: Wallet,
};

const Stat = ({ label, value, suffix }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wider text-slate-300">{label}</p>
    <p className="text-xl font-bold text-white">{value ?? '-'}{value != null && suffix ? suffix : ''}</p>
  </div>
);

export default function IntelligenceHubPage() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [fit, setFit] = useState(null);
  const [entered, setEntered] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, f] = await Promise.all([
        axios.get(`${API}/intelligence/summary`, getAuthHeader()),
        axios.get(`${API}/intelligence/career-fit`, getAuthHeader()),
      ]);
      setSummary(s.data);
      setFit(f.data);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <AppLayout><div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div></AppLayout>;
  }

  const s = summary || {};

  if (!entered) return (
    <AppLayout>
      <PageArrival
        testid="intelligence-arrival"
        eyebrow="Intelligence"
        title="This is where it all converges."
        subtitle={`Everything you've told us, who you are, the life you designed, what you can already do, gets measured against real federal data on wages, growth, automation and funding. ${s.target ? `Right now that's pointed at ${s.target}.` : 'Set a target and every engine below points at it.'}`}
        accent="#818cf8"
        second="#22d3ee"
        ctaLabel="Open my intelligence"
        narration={"This is your Intelligence hub, where everything converges. Your assessment, the life you designed, your skills and your target all get measured against real federal data, wages from the Bureau of Labor Statistics, growth and automation exposure by field, and the funding programmes you may qualify for. Career fit scores every option and tells you why. Transition intelligence shows the bridge from where you are to where you're going. Future of work tells you what's coming for your field. None of it is a guess, and every number names its source."}
        facts={[
          { icon: Sparkles, head: s.career_fit != null ? `Career fit ${s.career_fit}/100` : 'Career fit', sub: 'Every option scored, with every reason spelled out' },
          { icon: Brain, head: s.skills_match != null ? `Skills match ${s.skills_match}%` : 'Skills match', sub: 'What you already have against what the target needs' },
          { icon: TrendingUp, head: s.outlook?.growth ? `Outlook ${s.outlook.growth}` : 'Field outlook', sub: 'Growth, wages and automation exposure' },
        ]}
        modulesTitle="The engines behind this page"
        modules={[
          { icon: Sparkles, label: 'Career Fit', line: 'Interests, growth, AI exposure, pay against your own number, education reach, skills held.', hue: '#a78bfa' },
          { icon: TrendingUp, label: 'Transition Intelligence', line: 'Current role to target: transferable skills, the gaps, and the bridge with a timeline.', hue: '#22d3ee' },
          { icon: Target, label: 'Future of Work', line: 'What automation is doing to your field, and the skills rising against the ones falling.', hue: '#fbbf24' },
          { icon: Brain, label: 'Skills Intelligence', line: 'Gap score, evidence wallet, and military experience translated to civilian language.', hue: '#34d399' },
          { icon: GraduationCap, label: 'College Intelligence', line: 'Real cost, outcomes and what each major actually earns.', hue: '#60a5fa' },
          { icon: Wallet, label: 'Funding My Path', line: 'Federal and near-universal programmes matched to your situation.', hue: '#f472b6' },
        ]}
        footnote="Every figure on the pages behind this one names where it came from, BLS wages, O*NET occupations, federal College Scorecard, and published programme rules."
        onStart={() => { setEntered(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <PageHeader
        title="Intelligence"
        description="Where you are, where you're going, and the smartest route between the two, computed from federal occupation, wage and program data."
      />

      {/* Your FuturePath, the converged output */}
      <GlassCard className="mb-6 fp-rail" data-testid="futurepath-summary">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-cyan-300">Your FuturePath</p>
            <h3 className="text-xl font-bold text-white">
              {s.current_state?.path_label || 'Path not set yet'}
              {s.target ? <span className="text-slate-300 font-normal"> → {s.target}</span> : null}
            </h3>
            <p className="text-xs text-slate-300 mt-1">
              {(s.current_state?.archetypes || []).map(a => a.label).join(' · ') || 'Choose your path to activate the right engines'}
            </p>
          </div>
          <Link to="/app/choose-path" className="text-sm text-cyan-300 hover:text-cyan-200" data-testid="hub-change-path">Change my path</Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <Stat label="Career Fit" value={s.career_fit} suffix="/100" />
          <Stat label="Skills Match" value={s.skills_match} suffix="%" />
          <Stat label="Outlook" value={s.outlook?.growth} />
          <Stat label="Funding Matches" value={s.funding_matches} />
        </div>

        {(s.transferable_skills?.length > 0 || s.major_gaps?.length > 0) && (
          <div className="grid md:grid-cols-2 gap-4 mb-5">
            <div>
              <p className="text-xs uppercase tracking-wider text-emerald-300 mb-2">Skills that already transfer</p>
              <div className="flex flex-wrap gap-1.5">
                {(s.transferable_skills || []).map(sk => (
                  <span key={sk} className="px-2 py-0.5 rounded-full text-[11px] bg-emerald-500/10 border border-emerald-500/30 text-emerald-200">{sk}</span>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-amber-300 mb-2">Major gaps</p>
              <div className="flex flex-wrap gap-1.5">
                {(s.major_gaps || []).map(sk => (
                  <span key={sk} className="px-2 py-0.5 rounded-full text-[11px] bg-amber-500/10 border border-amber-500/30 text-amber-200">{sk}</span>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-slate-700">
          <div className="flex-1 min-w-[200px]">
            <p className="text-[11px] uppercase tracking-wider text-slate-300">Recommended next step</p>
            <p className="text-sm text-white">{s.recommended_next_step || 'Build your transition plan to get a recommended bridge'}</p>
            {s.estimated_transition && <p className="text-xs text-slate-300 mt-0.5">Estimated transition: {s.estimated_transition}</p>}
          </div>
          <Link to="/app/transition">
            <Button size="sm" data-testid="build-transition-btn">
              Build My Transition Plan <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-slate-300 mb-1">
            <span>Profile completeness</span><span>{s.profile_completeness}%</span>
          </div>
          <div className="fp-meter">
            <div className="fp-meter-fill" style={{ width: `${s.profile_completeness || 0}%` }} />
          </div>
        </div>
      </GlassCard>

      {/* Engines */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {(s.engines || []).map(e => {
          const Icon = ENGINE_ICON[e.key] || Compass;
          return (
            <Link key={e.key} to={e.path} data-testid={`engine-${e.key}`}
              className="fp-tile p-4">
              <div className="flex items-start justify-between">
                <div className="p-2 rounded-lg bg-slate-700/60"><Icon className="w-5 h-5 text-cyan-300" /></div>
                {e.ready
                  ? <span className="flex items-center gap-1 text-[11px] text-emerald-300"><CheckCircle className="w-3.5 h-3.5" /> Active</span>
                  : <span className="flex items-center gap-1 text-[11px] text-slate-300"><CircleDashed className="w-3.5 h-3.5" /> Needs input</span>}
              </div>
              <p className="text-sm font-semibold text-white mt-3">{e.label}</p>
            </Link>
          );
        })}
      </div>

      {/* Career Fit */}
      <GlassCard data-testid="career-fit-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Career Fit</h3>
          <span className="text-xs text-slate-300">Why each one fits, not just a list of job titles</span>
        </div>
        {!fit?.ready ? (
          <p className="text-sm text-slate-300">{fit?.message}</p>
        ) : (
          <div className="space-y-3">
            {fit.careers.map(c => (
              <div key={c.occupation} className="fp-tile p-4" data-testid={`fit-${c.soc_code}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-semibold text-white">{c.occupation}</p>
                    <p className="text-xs text-slate-300">{c.field}{c.median_wage ? ` · median $${c.median_wage.toLocaleString()}` : ''}</p>
                  </div>
                  <ScoreRing value={c.fit_score} label="Fit score" palette="violet" size={110} />
                </div>
                <ul className="mt-3 space-y-1">
                  {c.why.map((w, i) => (
                    <li key={i} className="text-xs text-slate-300 flex gap-2">
                      <span className="text-cyan-400">•</span>{w}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <p className="text-xs text-slate-300">{fit.completeness_note} Sources: {fit.sources.join(', ')}.</p>
          </div>
        )}
      </GlassCard>
    </AppLayout>
  );
}
